import { Component, HostBinding, input, InputSignal, output, OutputEmitterRef } from '@angular/core';
import { NgClass } from '@angular/common';

export type TBadgeType = 'primary' | 'secondary' | 'destructive' | 'outline' | 'warning' | 'pink' | 'blue' | 'success' | 'purple' | 'danger' | 'lime' | 'emerald' | 'cyan' | 'orange' | 'foreground';
export type TBadgeIconAlign = 'left' | 'right';
export type TFormat = 'default' | 'rounded';

/**
 * A reusable badge component for displaying styled labels with optional icons.
 * Supports various types (e.g., primary, success) and icon alignment.
 * @example
 * ```html
 * <lib-badge
 *   [type]="'success'"
 *   [icon]="'check'"
 *   [iconSize]="20"
 *   [iconAlign]="'left'"
 *   (iconClick)="handleIconClick()"
 * >Task Completed</lib-badge>
 * ```
 * ```typescript
 * // In the parent component
 * export class ParentComponent {
 *   handleIconClick() {
 *     console.log('Badge icon clicked!');
 *   }
 * }
 * ```
 */
@Component({
  selector: 'lib-badge',
  imports: [NgClass],
  templateUrl: './badge.component.html',
  styleUrl: './badge.component.scss',
  standalone: true,
})
export class BadgeComponent {
  /** The type of badge, determining its color and style. Defaults to 'primary'. */
  public readonly type: InputSignal<TBadgeType> = input<TBadgeType>('primary');

  /** The icon name (e.g., 'check') to display within the badge. Optional. */
  public readonly icon: InputSignal<string> = input<string>();

  /** The size of the icon in pixels. Defaults to 16. */
  public readonly iconSize: InputSignal<number> = input<number>(16);

  /** The alignment of the icon relative to the badge content. Defaults to 'right'. */
  public readonly iconAlign: InputSignal<TBadgeIconAlign> = input<TBadgeIconAlign>('right');

  /** Flag indicating if the badge should have rounded corners. Defaults to 'default'. */
  public readonly format: InputSignal<TFormat> = input<TFormat>('default');

  /** Flag to determine if the icon click event should stop propagation. Defaults to true. */
  public readonly iconStopPropagation: InputSignal<boolean> = input<boolean>(true);

  /** Event emitted when the icon is clicked. */
  public readonly iconClick: OutputEmitterRef<void> = output<void>();

  public readonly height = input<string>('24px');

  /**
   * The ARIA role for the badge, dynamically set based on interactivity.
   * Returns 'button' if the badge has an icon and an iconClick handler, otherwise 'status'.
   */
  @HostBinding('attr.role')
  get role(): string {
    return this.icon() && this.iconClick ? 'button' : 'status';
  }

  /**
   * The ARIA label for accessibility, describing the badge type and icon (if present).
   * Returns null if no meaningful label can be constructed.
   */
  @HostBinding('attr.aria-label')
  get ariaLabelValue(): string | null {
    const typeLabel = this.type();
    const iconLabel = this.icon() ? `with ${this.icon()} icon` : '';
    return `${typeLabel} badge ${iconLabel}`.trim() || null;
  }

  /**
   * Computes the CSS classes for the badge based on its type and icon alignment.
   * @returns A string of space-separated CSS classes.
   */
  public getBadgeClasses(): string {
    const baseClasses: string = 'w-fit max-w-full leading-[20px] flex justify-center items-center content-sm-semibold';
    const typeClasses: Record<TBadgeType, string> = {
      primary: 'bg-primary text-primary-foreground',
      success: '!text-success-foreground bg-success-fade border border-border-inverted',
      secondary: 'bg-secondary border border-border-inverted !text-secondary-foreground',
      destructive: 'bg-destructive text-destructive-foreground',
      outline: '!text-foreground hover:bg-accent-transparent dark:active:bg-accent-transparent border border-border-solid',
      warning: 'bg-warning-fade text-warning-foreground border border-border-inverted',
      pink: 'bg-pink-fade text-pink-foreground border border-border-inverted',
      blue: 'bg-blue-fade text-blue-foreground border border-border-inverted',
      purple: 'bg-purple-fade text-purple-foreground border border-border-inverted',
      danger: 'bg-danger-fade text-danger-foreground border border-border-inverted',
      lime: 'bg-lime-fade text-lime-foreground border border-border-inverted',
      emerald: 'bg-emerald-fade text-emerald-foreground border border-border-inverted',
      cyan: 'bg-cyan-fade text-cyan-foreground border border-border-inverted',
      orange: 'bg-orange-fade text-orange-foreground border border-border-inverted',
      foreground: 'bg-foreground text-background',
    };
    const alignmentClasses = {
      right: 'flex-row',
      left: 'flex-row-reverse',
    };
    const formatClasses = {
      default: 'px-[8px] rounded-[4px]',
      rounded: 'w-[24px] min-w-[24px] rounded-full',
    };

    return `${baseClasses} ${typeClasses[this.type()]} ${alignmentClasses[this.iconAlign()]} ${formatClasses[this.format()]}`;
  }

  /** Emits the iconClick event when the icon is clicked. */
  public onIconClicked(event?: Event): void {
    if (event && this.iconStopPropagation()) {
      event.stopPropagation();
    }
    this.iconClick.emit();
  }
}